import { useCallback, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { isAddressValid, useAddressState } from '@/components/AddressForm';
import AddressForm from '@/components/AddressForm/AddressForm';
import { GroupsSelector } from '@/components/GroupsSelector/GroupsSelector';
import InputPhone, {
  getCountryCallingCode,
  isPhoneValid,
  usePhoneState,
} from '@/components/InputPhone/InputPhone';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import InputSearch from '@/design_system/InputSearch';
import InputText from '@/design_system/InputText/InputText';
import { Label } from '@/design_system/Label/Label';
import Message from '@/design_system/Message';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconEdit from '@/icons/Edit.svg';
import IconTrash from '@/icons/Trash.svg';
import { formatStreet, formatZip } from '@/models/address';
import { formatPhone } from '@/models/partials/phone';
import { useUsers } from '@/models/user';
import {
  useGetWorkshopRegion,
  useUpdateWorkshop,
  useWorkshops,
  WorkshopFromGetMany,
} from '@/models/workshop';
import { useGeoIpCountry } from '@/services/auth';
import useDebouncedState from '@/utils/useDebouncedState';

const WORKSHOPS_PER_PAGE = 10;

const QualityControllers = ({
  values,
  onChange,
  isInvalid,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  isInvalid?: boolean;
}) => {
  const { t } = useLingui();
  const hasEmptyQualityControllerInput = values.some((value) => !value?.trim());

  return (
    <Label
      label={t({
        id: 'components.workshop-form.field.quality-controllers.label',
        message: 'Quality controllers',
      })}
    >
      <Stack gap="0.5rem">
        {values.map((item, index) => (
          <Stack key={index} row alignItems="center" gap="0.5rem">
            <InputText
              aria-label={t({
                id: 'components.workshop-form.field.quality-controllers.placeholder',
                message: "Quality controller's name",
              })}
              placeholder={t({
                id: 'components.workshop-form.field.quality-controllers.placeholder',
                message: "Quality controller's name",
              })}
              value={item}
              onChange={(newItem) =>
                onChange(values.map((oldItem, _index) => (index === _index ? newItem : oldItem)))
              }
              style={{ flex: 1 }}
              isInvalid={isInvalid && !item}
            />
            <Button
              iconOnly
              tooltip={t({
                id: 'components.workshop-form.field.quality-controllers.delete',
                message: `Remove ${item}`,
              })}
              ariaLabel={t({
                id: 'components.workshop-form.field.quality-controllers.delete',
                message: `Remove ${item}`,
              })}
              onPress={() => onChange(values.filter((_, _index) => index !== _index))}
              variant="secondary"
              size="large"
            >
              <IconTrash />
            </Button>
          </Stack>
        ))}
        {isInvalid && hasEmptyQualityControllerInput && (
          <Message type="error">
            <Trans id="components.workshop-form.field.quality-controllers.error">
              Please add a name for each quality controller
            </Trans>
          </Message>
        )}
        <Button
          variant="secondary"
          onPress={() => onChange([...values, ''])}
          style={{ alignSelf: 'flex-start' }}
        >
          <IconAdd />
          <Trans id="components.workshop-form.field.quality-controllers.add">
            Add a quality controller
          </Trans>
        </Button>
      </Stack>
    </Label>
  );
};

export const InternalWorkshops = () => {
  const { t } = useLingui();
  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), [setPage]);
  const [search, debouncedSearch, setSearch] = useDebouncedState('', 300, resetPage);

  const { data: { workshops, meta } = {} } = useWorkshops({
    limit: WORKSHOPS_PER_PAGE,
    offset: (page - 1) * WORKSHOPS_PER_PAGE,
    search: debouncedSearch,
    internal: true,
  });

  const noData = workshops && workshops?.length === 0;

  return (
    <Stack gap="40px">
      <Box padding="24px" gap="24px">
        <Stack row alignItems="center" justifyContent="space-between">
          <Title size="200" as="h2">
            <Trans id="settings.internal-workshops.title">
              Internal workshops of this organization
            </Trans>
          </Title>
        </Stack>
        <InputSearch
          ariaLabel={t({ id: 'settings.internal-workshops.search.label', message: 'Search' })}
          placeholder={t({
            id: 'settings.internal-workshops.search.placeholder',
            message: 'Search by name, address, contact name…',
          })}
          style={{ flex: 1, minWidth: 175, maxWidth: 500 }}
          size="medium"
          value={search}
          onChange={setSearch}
        />
        {noData && (
          <Paragraph>
            <Trans id="settings.internal-workshops.empty">No items for now</Trans>
          </Paragraph>
        )}
        {!noData && (
          <Table
            ariaLabel={t({
              id: 'settings.internal-workshops.table.label',
              message: 'Internal workshops',
            })}
            columnWidths="minmax(200px, 1fr) minmax(200px, 1fr) minmax(200px, 1fr) 80px auto"
          >
            <Header>
              <Row>
                <Column>
                  <Trans id="settings.internal-workshops.column.name">Name</Trans>
                </Column>
                <Column>
                  <Trans id="settings.internal-workshops.column.address">Address</Trans>
                </Column>
                <Column>
                  <Trans id="settings.internal-workshops.column.contact">Contact</Trans>
                </Column>
                <Column justifyContent="center">
                  <Trans id="settings.internal-workshops.column.users">Users</Trans>
                </Column>
                <Column
                  ariaLabel={t({
                    id: 'settings.internal-workshops.column.actions',
                    message: 'Actions',
                  })}
                />
              </Row>
            </Header>
            <Body>
              {workshops?.map((workshop) => (
                <InternalWorkshopRow key={workshop.id} workshop={workshop} />
              ))}
            </Body>
            <PaginationFooter
              page={page}
              itemsPerPage={WORKSHOPS_PER_PAGE}
              count={meta?.count}
              onPageChange={setPage}
            />
          </Table>
        )}
      </Box>
    </Stack>
  );
};

const InternalWorkshopRow = ({ workshop }: { workshop: WorkshopFromGetMany }) => {
  const { data: { meta } = {} } = useUsers({
    limit: 1,
    entityIds: [workshop.id],
  });

  return (
    <Row>
      <Cell>
        <Stack>
          <b>{workshop.name}</b>
          {workshop.groups.length > 0 && (
            <small>({workshop.groups.map((group) => group.name).join(', ')})</small>
          )}
        </Stack>
      </Cell>
      <Cell>
        <p>
          {workshop.address ? (
            <>
              {formatStreet(workshop.address) ?? '-'}
              <br />
              {formatZip(workshop.address) ?? '-'}
            </>
          ) : (
            '-'
          )}
        </p>
      </Cell>
      <Cell>
        <p>
          {workshop.address?.contactName ?? '-'}
          <br />
          {workshop.address?.contactEmail ?? '-'}
          <br />
          {formatPhone(workshop.contactInfo?.phone) ?? '-'}
        </p>
      </Cell>
      <Cell justifyContent="center">{meta?.count}</Cell>
      <Cell>
        <Stack row gap="4px">
          <UpdateInternalWorkshop workshop={workshop} />
        </Stack>
      </Cell>
    </Row>
  );
};

const UpdateInternalWorkshop = ({ workshop }: { workshop: WorkshopFromGetMany }) => {
  const { t } = useLingui();
  const getWorkshopRegion = useGetWorkshopRegion();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState(workshop.name);
  const [groups, setGroups] = useState(workshop.groups);
  const [address, setAddress] = useAddressState(workshop.address);
  const [phone, setPhone] = usePhoneState(workshop.contactInfo?.phone, workshop.address?.country);

  const initialQualityControllers: string[] = workshop.workshopConfig?.qualityControllers ?? [];

  const [qualityControllers, setQualityControllers] = useState(initialQualityControllers);

  const geoIpCountry = useGeoIpCountry();

  const { mutateAsync: updateWorkshop, isPending: isUpdateWorkshopPending } = useUpdateWorkshop();

  const label = t({
    id: 'settings.internal-workshops.edit-modal.trigger',
    message: `Edit ${workshop.name}`,
  });

  return (
    <Dialog
      isOpen={open}
      onOpenChange={(next) => {
        if (isUpdateWorkshopPending) return;

        setName(workshop.name);
        setAddress(workshop.address ?? { country: geoIpCountry });
        setPhone(
          workshop.contactInfo?.phone ?? {
            countryCode: getCountryCallingCode(geoIpCountry),
            number: '',
          }
        );

        setOpen(next);
      }}
      trigger={
        <Button variant="secondary" iconOnly size="small" ariaLabel={label} tooltip={label}>
          <IconEdit />
        </Button>
      }
      title={<Trans id="settings.internal-workshops.edit-modal.title">Edit workshop</Trans>}
    >
      <form
        style={{ overflow: 'auto' }}
        onSubmit={(evt) => {
          evt.preventDefault();

          if (qualityControllers.some((value) => !value?.trim())) {
            setError(
              t({
                id: 'components.workshop-form.field.quality-controllers.error',
                message: 'Please add a name for each quality controller',
              })
            );

            return;
          }

          if (!isAddressValid(address)) {
            setError(
              t({
                id: 'settings.internal-workshops.edit-modal.error.invalid-address',
                message: 'Invalid address',
              })
            );

            return;
          }

          if (!isPhoneValid(phone)) {
            setError(
              t({
                id: 'settings.internal-workshops.edit-modal.error.invalid-phone',
                message: 'Invalid phone number',
              })
            );

            return;
          }

          updateWorkshop({
            workshopId: workshop.id,
            name,
            groups,
            address,
            phone,
            qualityControllers,
          })
            .then(() => {
              setOpen(false);
              setError('');
            })
            .catch((err) => {
              setError(
                (err.message as string) ??
                  t({ id: '_general.error.unknown', message: 'Unknown error' })
              );
            });
        }}
      >
        <main>
          <Stack gap="24px">
            <InputText
              label={
                <Trans id="settings.internal-workshops.edit-modal.field.name.label">Name</Trans>
              }
              type="text"
              placeholder={t({
                id: 'settings.internal-workshops.edit-modal.field.name.placeholder',
                message: 'Name of the workshop',
              })}
              value={name}
              onChange={setName}
              isRequired
              isDisabled={isUpdateWorkshopPending}
              isInvalid={!!error}
            />

            <GroupsSelector groups={groups} setGroups={setGroups} />

            <InputPhone
              label={
                <Trans id="settings.internal-workshops.edit-modal.field.phone.label">Phone</Trans>
              }
              value={phone}
              onChange={setPhone}
              defaultCountry={workshop.address?.country}
            />

            <AddressForm value={address} onChange={setAddress} />

            <InputText
              label={
                <Trans id="settings.internal-workshops.edit-modal.field.region.label">Region</Trans>
              }
              type="text"
              value={getWorkshopRegion(workshop)}
              isRequired
              isDisabled
            />

            <QualityControllers values={qualityControllers} onChange={setQualityControllers} />

            {error && (
              <Paragraph weight="medium" color="danger" align="center">
                {error}
              </Paragraph>
            )}
          </Stack>
        </main>
        <Footer>
          <FooterButtons>
            <Button
              variant="primary"
              size="small"
              type="submit"
              isLoading={isUpdateWorkshopPending}
            >
              <Trans id="settings.internal-workshops.edit-modal.submit">Save</Trans>
            </Button>
          </FooterButtons>
        </Footer>
      </form>
    </Dialog>
  );
};
